import {useDisclosure} from '@mantine/hooks';
import React, {useEffect, useMemo, useState} from 'react';

import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';

import {useAlert} from 'src/shared/hooks/useAlert';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';

import type {
  SafetyEventRequest,
  SafetyEventResponse,
} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  ModuleEnum,
  NoteModuleEnum,
  NotificationModuleEnum,
  SafetyEventStateEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateSafetyEventMutation,
  useDeleteSafetyEventMutation,
  useGetSafetyEventQuery,
  useUpdateSafetyEventMutation,
} from 'src/redux/queries/safetyEvent';
import {InfoCard} from 'src/design/molecules/InfoCard';
import {IconSize, InfoIcon} from 'src/design/atoms/icons/CommonIcons';
import {ActionCard} from 'src/design/molecules/ActionCard';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {AcknowledgementTransitionButton} from 'src/app/forms/AcknowledgementTransitionButton';
import {BackBar} from 'src/app/layout/BackBar';
import {DetailGrid} from 'src/design/atoms/DetailGrid';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconInfoCircle} from '@tabler/icons-react';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SidebarLinkSafetyEvents} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {NotesModule} from 'src/app/notes/NotesModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {identifierFieldName} from 'src/types/FormV2';
import {useGetPersonnelSelectOptionsQuery} from 'src/redux/queries/personnel';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useSafetyEventPermissions} from 'src/shared/auth/permissions/useSafetyEventPermissions';
import {SafetyEventCustomOperations} from 'src/shared/auth/permissions/customOperations';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useApiCall} from 'src/shared/hooks/api/useApiCall';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useStateTransitions} from 'src/shared/hooks/api/useStateTransitions';
import type {
  IRouteService,
  ViewModeType,
} from 'src/shared/services/RouterService/IRouteService';
import {GetStatePillFromStateMachine} from 'src/shared/utils/stateMachineUtils';
import type {IFieldType} from 'src/types/FieldOption';
import type {ISelectOption} from 'src/types/Form';
import type {
  IStateMachine,
  IStateMachineTransition,
} from 'src/types/StateMachine';
import {
  coerceSafetyEventCurrencyValues,
  SafetyEventMeta,
} from './safetyEventHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type SafetyEventPermissionKey = keyof typeof SafetyEventCustomOperations;

const editOverrideKeyByOpName = Object.fromEntries(
  Object.entries(SafetyEventCustomOperations).map(([key, opName]) => [
    opName,
    key as SafetyEventPermissionKey,
  ])
) as Record<string, SafetyEventPermissionKey>;

const navigationConfig: PageNavigationConfig = {
  entityName: 'Safety Event',
  entityNamePlural: 'Safety Events',
  listPath: PathUtility.SafetyEvent(),
  viewPath: id => PathUtility.SafetyEventView(String(id), 'content'),
};

const CreateDefaultSafetyEvent = (): Partial<SafetyEventResponse> => ({
  state: SafetyEventStateEnum.Assemble,
  oshaRecordable: false,
  oshaLostTime: false,
  oshaRestrictionTime: false,
  oshaFatality: false,
  oshaEmergencyRoomTreatment: false,
  oshaHospitalizedOvernight: false,
  propertyDamageInterruption: false,
});

const mapFormValuesToSafetyEventRequest = (
  values: IFormDataV2
): SafetyEventRequest => {
  const body: IFormDataV2 = {
    ...values,
    shortDescription: values[identifierFieldName],
  };
  Object.keys(body).forEach(key => {
    if (body[key] === '') {
      body[key] = undefined;
    }
  });
  return body as SafetyEventRequest;
};

const formatDate = (date: Date | string | undefined): string => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export type SafetyEventViewType =
  | 'content'
  | 'notifications'
  | 'notes'
  | 'attachments'
  | 'audit'
  | ''
  | ':view';

export interface SafetyEventFormProps {
  id?: number;
  mode: ViewModeType;
  view?: SafetyEventViewType;
  router: IRouteService;
}
export const SafetyEventForm: React.FC<SafetyEventFormProps> = ({
  id,
  mode,
  view = 'content',
  router,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [item, setItem] = useState<SafetyEventResponse>();
  const viewFormData = useMemo(
    () => ({
      ...toFormDataV2(item),
      [identifierFieldName]: item?.shortDescription,
      // View mode reads project fields from formData['project'], but the
      // response only carries the flat projectId/projectName pair.
      project: item?.projectId
        ? {id: item.projectId, name: item.projectName}
        : undefined,
    }),
    [item]
  );

  const [fieldValues, isFieldValuesLoading] = useApiCall({
    url: ApiEndpoints.getSafetyEventFieldValues,
    method: 'GET',
    cachingMethod: 'local',
  });
  const [machineBase, isMachineLoading] = useStateMachine({
    url: ApiEndpoints.getSafetyEventStateMachine,
  });
  const machine =
    mode === 'view'
      ? machineBase
      : machineBase
      ? ({
          states: machineBase.states,
          transitions: machineBase.transitions.map(t => ({
            ...t,
            title: `Save & ${t.title}`,
          })),
        } as IStateMachine)
      : undefined;

  const auditPerms = useAuditLogPermissions();
  const safetyEventPerms = useSafetyEventPermissions();

  const {
    data: personnel,
    isFetching: isPersonnelFetching,
    isLoading: isPersonnelLoading,
  } = useGetPersonnelSelectOptionsQuery({
    start: 0,
    length: 10000,
    active: [UserStatusEnum.Active],
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    superviserOnly: false,
  });

  const [isSavePending, setIsSavePending] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);

  const {
    data: fetchedItem,
    isLoading: isDataLoading,
    refetch: refetchSafetyEvent,
  } = useGetSafetyEventQuery(id!, {
    skip: id === undefined || isNaN(+id),
  });

  const isLoading =
    isDataLoading ||
    isMachineLoading ||
    isFieldValuesLoading ||
    isPersonnelFetching ||
    isPersonnelLoading;

  const formMeta = useMemo<IFormFieldV2[]>(() => {
    if (isLoading || !fieldValues || !personnel) {
      return [];
    }
    if (mode !== 'create' && !item) {
      return [];
    }
    return SafetyEventMeta(
      fieldValues as IFieldType[],
      (personnel?.data ?? []) as ISelectOption[]
    );
  }, [item, fieldValues, personnel, isLoading, mode]);

  const form = useEntitySyncedForm({
    formMeta,
    initialData: CreateDefaultSafetyEvent(),
    entity: fetchedItem,
    onEntityArrival: setItem,
    toFormData: entity =>
      coerceSafetyEventCurrencyValues(
        toFormDataV2({
          ...CreateDefaultSafetyEvent(),
          ...entity,
          [identifierFieldName]: entity.shortDescription,
        })
      ),
  });

  useEffect(() => {
    const subscription = form.methods.watch((values, {name}) => {
      if (name && !isBlocking) {
        setIsBlocking(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.methods, isBlocking]);

  const [createSafetyEvent] = useCreateSafetyEventMutation();
  const [updateSafetyEvent] = useUpdateSafetyEventMutation();
  const [deleteSafetyEvent, {isLoading: isDeletePending}] =
    useDeleteSafetyEventMutation();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const handleSave = async () => {
    try {
      setIsSavePending(true);
      const values = form.methods.getValues();
      const body = mapFormValuesToSafetyEventRequest(values);

      const isCreate = id === undefined;
      const res = isCreate
        ? await createSafetyEvent(body).unwrap()
        : await updateSafetyEvent(body).unwrap();

      setIsBlocking(false);
      form.methods.reset(values);
      setAlert(
        isCreate
          ? 'Safety Event was successfully created'
          : 'Safety Event was successfully updated',
        'success'
      );

      navigate(router.getViewRoute(String(res.id)), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const currentState = machine?.states.find(s => s.id === item?.state);
  const overrideKey = currentState?.editOverridePermission
    ? editOverrideKeyByOpName[currentState.editOverridePermission]
    : undefined;
  const hasEditOverride =
    overrideKey !== undefined && safetyEventPerms[overrideKey] === true;
  const canEdit =
    isViewMode &&
    safetyEventPerms.update &&
    (currentState?.isEditable === true || hasEditOverride);

  const canDelete =
    isViewMode && safetyEventPerms.delete && currentState?.isDeletable === true;

  const handleEdit = () => navigate(router.getEditRoute(String(id)));
  const handleDelete = async () => {
    confirmDeleteHandlers.close();
    if (id === undefined) {
      return;
    }
    try {
      await deleteSafetyEvent(id).unwrap();
      setAlert('Safety Event was successfully deleted', 'success');
      navigate(PathUtility.SafetyEvent());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };
  const {goBack} = useBackNavigation(
    isEditMode && id
      ? router.getViewRoute(String(id))
      : PathUtility.SafetyEvent()
  );
  const handleCancel = () => {
    setIsBlocking(false);
    goBack();
  };

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: item?.shortDescription,
      }),
    [mode, id, item?.shortDescription]
  );

  const statePills = machine
    ? GetStatePillFromStateMachine(item?.state, machine)
    : [];

  // Use the state transitions hook for sidebar workflow actions
  // Use `machine` (not `machineBase`) to get "Save & ..." titles in edit mode
  const {availableTransitions, isTransitioning, executeTransition} =
    useStateTransitions({
      stateMachine: machine,
      stateId: item?.state ?? SafetyEventStateEnum.Assemble,
      transitionBaseUrl: `${ApiEndpoints.transitionSafetyEvent}/${id}/transition`,
      module: ModuleEnum.SafetyEvent,
      onTransitionComplete: () => refetchSafetyEvent(),
      invalidateTagsOnComplete: ['SafetyEvents', 'TaskAssignments'],
      onTransitioning: async () => {
        // In edit mode, validate and save first before transitioning
        if (!isViewMode) {
          const isValid = await form.validateForm();
          if (!isValid) {
            return {continue: false};
          }
          await handleSave();
        }
        return {continue: true};
      },
    });

  const extensionTabs = useExtensionTabs(ModuleEnum.SafetyEvent, id ?? 0);

  const {RenderedTabsComponent} = useUrlQueryTabV2<SafetyEventViewType>({
    view: (view ?? '') as SafetyEventViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: isViewMode ? (
          <FormViewComponentV2 formData={viewFormData} formMeta={formMeta} />
        ) : (
          <ProactForm form={form}>
            <ProactFormGrid />
          </ProactForm>
        ),
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: mode !== 'view' || !safetyEventPerms.notifications,
        component: (
          <>
            <NotificationsModule
              entityId={item?.id}
              notificationType={NotificationModuleEnum.SafetyEvent}
            />
          </>
        ),
      },
      {
        view: 'notes',
        label: 'Notes',
        disabled: mode !== 'view' || !safetyEventPerms.notes,
        component: (
          <>
            {item && (
              <NotesModule
                noteType={NoteModuleEnum.SafetyEvent}
                entityId={item.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        disabled: mode !== 'view' || !safetyEventPerms.attachments,
        component: (
          <>
            {item && (
              <AttachmentsModule
                attachmentType={AttachmentModuleEnum.SafetyEvent}
                entityId={item.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: mode !== 'view' || !auditPerms.view,
        component: item?.id && (
          <AuditModule
            entityId={String(item.id)}
            entityType={EntityIdentifierEnum.SafetyEvent}
          />
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view => {
      router.setParameter('view', view);
      navigate(router.getViewRoute(String(id)), {replace: true});
    },
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const getTitle = () => {
    if (item) {
      return item.shortDescription ?? 'Unknown';
    }
    return !id ? 'New Safety Event' : 'Item not found';
  };

  return (
    <Stack gap={0}>
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkSafetyEvents.icon}
          subtitle={item?.internalIdentifier}>
          {getTitle()}
        </PageHeader.Title>

        <PageHeader.Badges>
          {statePills.map((pill, index) => (
            <PageHeader.Badge key={index} color={pill.color} variant="light">
              {pill.text}
            </PageHeader.Badge>
          ))}
          {isEditMode && (
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          )}
        </PageHeader.Badges>

        <PageHeader.Actions>
          {canEdit && (
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          )}

          {canDelete && (
            <PageHeader.Action
              variant="delete"
              onClick={confirmDeleteHandlers.open}
              loading={isDeletePending}>
              Delete
            </PageHeader.Action>
          )}

          {!isViewMode && (
            <>
              <PageHeader.Action variant="cancel" onClick={handleCancel}>
                Cancel
              </PageHeader.Action>
              <PageHeader.Action
                variant={isCreateMode ? 'create' : 'save'}
                onClick={handleValidateAndSave}
                loading={isSavePending}>
                {isCreateMode ? 'Create Safety Event' : 'Save Changes'}
              </PageHeader.Action>
            </>
          )}
        </PageHeader.Actions>
      </PageHeader>

      <DetailGrid>
        <DetailGrid.Main>
          {RenderedTabsComponent}

          {/* Save buttons at bottom of form in edit/create mode */}
          {!isViewMode && (
            <Group mt="md">
              <PageHeader.Action variant="cancel" onClick={handleCancel}>
                Cancel
              </PageHeader.Action>
              <PageHeader.Action
                variant={isCreateMode ? 'create' : 'save'}
                onClick={handleValidateAndSave}
                loading={isSavePending}>
                {isCreateMode ? 'Create Safety Event' : 'Save Changes'}
              </PageHeader.Action>
            </Group>
          )}
        </DetailGrid.Main>

        <DetailGrid.Sidebar>
          {item?.linkedFormId && (
            <Alert
              variant="light"
              color="blue"
              title="Linked Form Submission"
              icon={<IconInfoCircle />}>
              <Text size="sm" mb="xs">
                This Safety Event is linked to a form submission.
              </Text>
              <Anchor
                size="sm"
                href={PathUtility.FormSubmission(
                  item?.linkedForm?.formDefinitionId as number,
                  item?.linkedFormId,
                  'content'
                )}
                target="_blank"
                rel="noopener noreferrer">
                Open Form Submission
              </Anchor>
            </Alert>
          )}

          {/* Safety Event Info */}
          {item && (
            <InfoCard
              title="Safety Event Info"
              icon={<InfoIcon size={IconSize.sm} />}
              compact>
              <InfoCard.Row label="ID" value={item.internalIdentifier} />
              <InfoCard.Row
                label="Incident Date"
                value={formatDate(item.incidentAt)}
              />
              <InfoCard.Row label="Location" value={item.location || '—'} />
            </InfoCard>
          )}

          {/* State Transitions - Only show when there are available transitions */}
          {availableTransitions.length > 0 && (
            <ActionCard>
              <ActionCard.Description>
                {isViewMode
                  ? 'Move this safety event to the next stage.'
                  : 'Save and move to the next stage.'}
              </ActionCard.Description>
              {availableTransitions.map(
                (transition: IStateMachineTransition) => (
                  <AcknowledgementTransitionButton
                    key={transition.name}
                    transition={transition}
                    executeTransition={executeTransition}
                    renderButton={onClick => (
                      <ActionCard.Button
                        onClick={onClick}
                        loading={isTransitioning}
                        color={transition.color ?? 'blue'}>
                        {transition.title}
                      </ActionCard.Button>
                    )}
                  />
                )
              )}
            </ActionCard>
          )}
        </DetailGrid.Sidebar>
      </DetailGrid>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={confirmDeleteHandlers.close}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this safety event?"
        body="The safety event will be permanently deleted, along with its notes and attachments."
      />
    </Stack>
  );
};
